import { SEOHead } from '@/components/seo/SEOHead';
import { Timer, MessageCircle } from 'lucide-react';
export const LeadNurturePage = () => {
  return (
    <>
      <SEOHead
        title="Roofing Lead Nurturing & Automated Follow-Up"
        description="Stop losing roofing leads to slow follow-up. Rooflyzer automates SMS and email sequences so your pipeline never goes cold."
        canonicalUrl="/nurture"
        keywords="roofing lead nurturing, roofing follow-up automation, roofing CRM automation"
        ogImage="/social-card.svg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Lead Nurturing',
              item: 'https://rooflyzer.pro/nurture',
            },
          ],
        }}
      />

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">No Lead Left Behind.</h1>
          <p className="text-xl text-white/90">
            The average homeowner calls 3 roofers. The one who follows up fastest wins. Rooflyzer
            automates that speed for you.
          </p>
        </div>

        {/* Workflow Diagram Section */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            How an Automated Drip Works
          </h2>

          <div className="relative flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Step 1 */}
            <div className="w-full md:w-1/3 p-6 bg-slate-900/60 border border-slate-800 rounded-xl relative">
              <div className="absolute top-0 right-0 -mr-2 -mt-2 w-8 h-8 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center font-mono font-bold text-white text-sm">
                1
              </div>
              <h3 className="text-lg font-bold text-white mb-2">New Lead Enters</h3>
              <p className="text-white/80 text-sm">
                Customer fills out your form or you drop a pin while canvassing.
              </p>
            </div>

            {/* Connector */}
            <div className="hidden md:block w-16 h-px bg-slate-700"></div>
            <div className="md:hidden h-8 w-px bg-slate-700"></div>

            {/* Step 2 */}
            <div className="w-full md:w-1/3 p-6 bg-slate-900/60 border border-slate-800 rounded-xl relative">
              <div className="absolute top-0 right-0 -mr-2 -mt-2 w-8 h-8 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center font-mono font-bold text-white text-sm">
                2
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Instant SMS + Email</h3>
              <p className="text-white/80 text-sm">
                "Hi [Name], thanks for contacting [Company]. When can we stop by for an inspection?"
              </p>
            </div>

            {/* Connector */}
            <div className="hidden md:block w-16 h-px bg-slate-700"></div>
            <div className="md:hidden h-8 w-px bg-slate-700"></div>

            {/* Step 3 */}
            <div className="w-full md:w-1/3 p-6 bg-slate-900/60 border border-slate-800 rounded-xl relative">
              <div className="absolute top-0 right-0 -mr-2 -mt-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center font-mono font-bold text-slate-950 text-sm">
                3
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Follow-up Drip</h3>
              <p className="text-white/80 text-sm">
                If no reply in 24h, system sends a gentle reminder. If they reply, the automation
                stops.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="bg-slate-900/60 p-8 rounded-2xl border border-slate-800">
            <MessageCircle className="w-8 h-8 text-emerald-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">2-Way SMS Dashboard</h3>
            <p className="text-white/80">
              Reply to texts directly from Rooflyzer. Keep your personal number private. All history
              is saved to the customer's file.
            </p>
          </div>
          <div className="bg-slate-900/60 p-8 rounded-2xl border border-slate-800">
            <Timer className="w-8 h-8 text-emerald-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Smart Delays</h3>
            <p className="text-white/80">
              Send messages during business hours only. Add natural delays (e.g., "Wait 15 min") so
              it doesn't look like a bot.
            </p>
          </div>
        </div>
        {/* Product Screenshot */}
        <section className="mt-16 max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
            <img
              src="/product-frames/leads.webp"
              alt="Rooflyzer — Lead management with automated nurture sequences"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default LeadNurturePage;
